import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

const WhatsAppSettings = () => {
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    whatsapp_messaging_enabled: false,
    auto_replies_enabled: false,
    lead_number_masking_enabled: false,
    is_whatsapp_connected: false,
  });

  useEffect(() => {
    fetchWhatsAppSettings();
  }, []);

  const fetchWhatsAppSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/integrations/whatsapp/settings/');
      setSettings({
        whatsapp_messaging_enabled: response.data.whatsapp_messaging_enabled || false,
        auto_replies_enabled: response.data.auto_replies_enabled || false,
        lead_number_masking_enabled: response.data.lead_number_masking_enabled || false,
        is_whatsapp_connected: response.data.is_whatsapp_connected || false,
      });
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
      if (error.response?.status !== 404) {
        openErrorModal('Failed to load WhatsApp settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (settingKey) => {
    const newValue = !settings[settingKey];

    // Validation: Lead Number Masking requires WhatsApp to be connected and messaging enabled
    if (settingKey === 'lead_number_masking_enabled') {
      if (!settings.is_whatsapp_connected) {
        openErrorModal('Please connect WhatsApp first before enabling Lead Number Masking');
        return;
      }
      if (!settings.whatsapp_messaging_enabled) {
        openErrorModal('Please enable WhatsApp Messaging before enabling Lead Number Masking');
        return;
      }
    }

    // Validation: Auto Replies requires WhatsApp Messaging to be enabled
    if (settingKey === 'auto_replies_enabled' && newValue && !settings.whatsapp_messaging_enabled) {
      openErrorModal('Please enable WhatsApp Messaging before enabling Auto Replies');
      return;
    }

    try {
      showLoader('Updating settings...');
      
      const response = await api.patch('/lms/integrations/whatsapp/settings/', {
        [settingKey]: newValue,
      });

      setSettings(prev => ({
        ...prev,
        [settingKey]: newValue,
      }));

      hideLoader();
      openSuccessModal('WhatsApp settings updated successfully');
    } catch (error) {
      hideLoader();
      openErrorModal(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to update WhatsApp settings'
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <WhatsAppIcon sx={{ fontSize: 32, color: '#25D366' }} />
          <Typography variant="h5" gutterBottom fontWeight="bold">
            WhatsApp Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure WhatsApp messaging and lead number masking preferences for your account.
        </Typography>
      </Box>

      {!settings.is_whatsapp_connected && (
        <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            WhatsApp is not connected. Please connect WhatsApp in the Integrations tab to enable these settings.
          </Typography>
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Messaging Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.whatsapp_messaging_enabled}
                  onChange={() => handleToggle('whatsapp_messaging_enabled')}
                  disabled={!settings.is_whatsapp_connected}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable WhatsApp Messaging for this Account
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Allow sending and receiving WhatsApp messages through Salesgro
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.auto_replies_enabled}
                  onChange={() => handleToggle('auto_replies_enabled')}
                  disabled={!settings.is_whatsapp_connected || !settings.whatsapp_messaging_enabled}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable Auto Replies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automatically send predefined replies to incoming WhatsApp messages
                  </Typography>
                </Box>
              }
            />
            {!settings.whatsapp_messaging_enabled && settings.is_whatsapp_connected && (
              <Alert severity="info" sx={{ mt: 1, ml: 4 }}>
                <Typography variant="caption">
                  Enable WhatsApp Messaging first to use Auto Replies
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Privacy Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Lead Number Masking
            </Typography>
            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
              Lead Number Masking helps protect customer privacy by hiding phone numbers from team members 
              who don't need access to them in the mobile app.
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Configuration:</strong> Lead Number Masking is configured per agent in the 
              <strong> Users & Teams</strong> tab. It only works when WhatsApp is connected and messaging is enabled.
            </Typography>
          </Alert>

          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="caption">
              <strong>How it works:</strong> When enabled for an agent, lead phone numbers will be masked 
              in their mobile app view. They will only see masked numbers (e.g., ****5678) unless they have 
              explicit permission to view full numbers.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppSettings;
